<template>
  <div
    v-show="isShow"
    id="acCookie_notification"
    class="acCookie"
    data-cy="acCookie_modal"
  >
    <div class="acCookie_wrapper">
      <div class="acCookie_content">
        <div class="acCookie_row acCookie_title">
          Куки это важно, куки это нужно.
        </div>
        <div class="acCookie_row acCookie_text">
          {{ $t('cookie.agreement') }}
          <a v-if="cookiePolicyLink" :href="cookiePolicyLink">{{ $t('cookie.policy') }}</a>
        </div>
      </div>

      <div class="acCookie_actions">
        <ac-button
          class="acCookie_button"
          data-cy="acCookie_modal_dismiss"
          variant="outlined"
          @click="accept('dismiss')"
        >
          {{ $t('cookie.dismiss') }}
        </ac-button>

        <ac-button
          class="acCookie_button"
          data-cy="acCookie_modal_settings"
          variant="outlined"
          @click="settings"
        >
          {{ $t('cookie.settings') }}
        </ac-button>

        <ac-button
          class="acCookie_button"
          data-cy="acCookie_modal_accept"
          variant="outlined"
          @click="accept('acceptAll')"
        >
          {{ $t('cookie.agreeAll') }}
        </ac-button>
      </div>
    </div>
  </div>

  <ac-modal v-if="isShowModal" :model-value="true">
    <cookie-modal-content
      @close="closeSettings"
      @accept="accept('accept')"
      @dismiss="accept('dismiss')"
      show-title
      show-close-btn
    >
      <template #blocks>
        <cookie-modal-block
          v-for="(cookie, key) in mappedCookies"
          :key="`cookiePolicy-${key}`"
          :cookie="cookie"
          @toggle="toggleCookie($event)"
        />
      </template>
    </cookie-modal-content>
  </ac-modal>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import { useStore } from '@web/store'
import AcButton from '@ui-kit/components/AcButton.vue'
import CookieModalContent from '@ui-kit/components/AcCookie/CookieModalContent.vue'
import CookieModalBlock from '@ui-kit/components/AcCookie/CookieModalBlock.vue'

import Cookies from 'js-cookie'
import sentry from '@web/common/sentry'
import Logger from '@web/common/Logger'

import { cookiePolicyLink } from '@web/common/ExternalConfig/getters'
import AcModal from '@ui-kit/components/AcModal.vue'
import Analytics from '@web/services/Analytics/Analytics'

export default defineComponent({
  name: 'AcCookie',
  components: {
    CookieModalContent,
    AcModal,
    CookieModalBlock,
    AcButton
  },
  setup () {
    const store = useStore()

    const KEY = 'is_cookies_policy_set'
    const isCookiesPolicySet = ref(Cookies.get(KEY))

    const isShowModal = ref(false)
    const localCookiePolicy = ref({
      essential: true,
      analytics: false,
      marketing: false
    })

    const cookiesIsEnabled = computed(() => store.getters['cookie/isEnabled'])
    const cookies = computed(() => store.getters['cookie/cookies'])
    const cookiePolicy = computed(() => store.getters['cookie/policy'])
    const mappedCookies = computed(() => {
      return Object.keys(cookiePolicy.value)
        .map((type) => {
          const _cookies = Object.values(cookies.value).find((c) => c.key === type)
          return _cookies
            ? {
                ..._cookies,
                enabled: cookiePolicy.value[type]
              }
            : undefined
        })
        .filter(c => c)
    })

    const isShow = computed(() => !isCookiesPolicySet.value && !isShowModal.value && cookiesIsEnabled.value)

    async function accept (type: 'dismiss' | 'accept' | 'acceptAll') {
      if (type === 'dismiss') {
        await store.dispatch('cookie/updatePolicy', {
          essential: true,
          analytics: false,
          marketing: false
        })
      } else if (type === 'acceptAll') {
        await store.dispatch('cookie/updatePolicy', {
          essential: true,
          analytics: true,
          marketing: true
        })
      } else {
        await store.dispatch('cookie/updatePolicy', localCookiePolicy.value)
      }
      Analytics.send({
        category: 'cookie_modal',
        action: `cookie_${type}`,
        label: 'platform'
      })
      Cookies.set(KEY, 'true', { sameSite: 'Strict', expires: 365 })
      isCookiesPolicySet.value = true
      isShowModal.value = false
    }

    function settings () {
      Analytics.send({
        category: 'cookie_modal',
        action: 'cookie_settings',
        label: 'platform'
      })
      isShowModal.value = true
    }

    function closeSettings () {
      isShowModal.value = false
    }

    async function init () {
      try {
        await store.dispatch('cookie/fetchCookies')
        await store.dispatch('cookie/fetchPolicy')
      } catch (e) {
        sentry.captureException(e)
        Logger.error(e)
      }
    }

    // if (!isCookiesPolicySet.value) {
    init()
    // }

    function toggleCookie (type: 'essential' | 'analytics' | 'marketing') {
      if (type !== 'essential') {
        localCookiePolicy.value[type] = !localCookiePolicy.value[type]
      }
    }

    watch(cookiePolicy, () => {
      localCookiePolicy.value = cookiePolicy.value
    }, {
      immediate: true
    })

    return {
      cookiePolicyLink: cookiePolicyLink(),
      isShow,
      isShowModal,
      mappedCookies,
      localCookiePolicy,
      accept,
      settings,
      closeSettings,
      toggleCookie
    }
  }
})
</script>

<style lang="scss" scoped>
.acCookie {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  z-index: 99998;
  background-color: var(--ac-color-white);
  border-radius: 8px 8px 0 0;
  overflow: hidden;

  &_wrapper {
    margin: 0 auto;
    max-width: 1200px;
    width: 100%;
    padding: 10px 30px;
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
    justify-content: space-between;

    @media (max-width: 962px) {
      flex-flow: column nowrap;
    }
  }

  &_content {
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: flex-start;
  }

  &_row {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
  }

  &_title {
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 130%;
    color: var(--ac-color-black);
    margin-bottom: 6px;
    display: none;
  }

  &_text {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: var(--ac-color-black);
    display: inline;
  }

  &_actions {
    display: flex;
    flex-flow: row nowrap;
    margin-left: 10px;

    @media (max-width: 962px) {
      margin-top: 18px;
      width: 100%;
      margin-left: 0;
    }

    @media (max-width: 600px) {
      flex-flow: column nowrap;
    }
  }

  &_button {
    margin-right: 12px;
    white-space: nowrap;
    min-width: 135px;

    &:last-child {
      margin-right: 0;
      margin-bottom: 0;
    }

    @media (max-width: 962px) {
      width: 100%;
    }

    @media (max-width: 600px) {
      margin-right: 0;
      margin-bottom: 12px;
    }
  }
}

.modal-cookie-settings {
  background-color: var(--ac-color-white);
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  z-index: 99999;
  width: 800px;
  user-select: none;
  overflow: hidden;
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  justify-content: flex-start;
}

.header {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #D9D9D9;
  padding: 28px 54px 20px 54px;
  width: 100%;

  &_close {
    cursor: pointer;
  }
}

.body {
  padding: 20px 44px 20px 54px;
  width: 100%;
  overflow: hidden;
  display: flex;

  &_wrapper {
    padding-right: 8px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
      background: transparent;
    }
    &::-webkit-scrollbar-button:vertical:decrement {
      background: transparent;
      height: 0;
    }
    &::-webkit-scrollbar-button:vertical:increment {
      background: transparent;
      height: 0;
    }
    &::-webkit-scrollbar-track {
      background: #DBE8FE;
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--ac-color-blue);
      border-radius: 10px;
      background-clip: content-box;
      box-sizing: content-box;
      box-shadow: inset 0 0 10px 10px transparent;
    }
  }

  &_row {
    border-bottom: 1px solid #D9D9D9;
    margin: 10px 0;
  }

  &_block {
    padding: 12px 0;
    border-bottom: 1px solid #D9D9D9;

    &:last-child {
      border-bottom: 0;
    }

    &_header {
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: 140%;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
    }

    &_title {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: flex-start;
    }

    &_titleIcon {
      margin-right: 14px;
      cursor: pointer;
    }

    &_body {
      padding: 0 56px 0 43px;
    }

    &_hidden {
      margin-top: 12px;
    }
  }
}

.footer {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #D9D9D9;
  padding: 20px 54px 28px 54px;
  width: 100%;
}
</style>
